const waterSpotlightData = {
    displayName: 'water',
    // Manually update when the spotlight's content changes.
    publishedOn: 'January 2026',
    hero: {
        image: require('../../../assets/img/spotlights/coloradoRiver.jpg'),
        title: 'Water Scarcity and Allocation',
        description:
            'Water is the foundation of all sustainability. Colorado produces an average of 16 million acre-feet ' +
            'of water per year, yet must send roughly two-thirds downstream to honor interstate compact obligations. ' +
            "With the Colorado River in crisis and the state's population projected to nearly double by 2050, " +
            "understanding water scarcity is more critical than ever. This spotlight explores the data behind Colorado's " +
            'water challenges and puts key resources at your fingertips.',
        tagline: 'Without water, no sustainability efforts can exist.',
    },

    sections: {
        dataSources: {
            title: 'Explore Key Data Sources',
            description:
                "Discover datasets that track Colorado's water use, conservation, and sustainability efforts.",
        },
        dataProducts: {
            title: 'Explore Water Products and Tools',
            description:
                'These interactive tools and reports showcase how water impacts life across Colorado.',
        },
        starterPrompts: {
            title: "Don't Know Where to Start? Ask Bili!",
            description:
                "Here are some ways to explore Colorado's water story:",
        },
    },

    whyItMatters: {
        title: 'Why Water Matters in Colorado',
        intro:
            'Colorado is a headwaters state at the center of one of the most pressing ' +
            "environmental challenges in the American West. Understanding water scarcity here isn't " +
            'just academic. It affects agriculture, communities, recreation, and the future ' +
            'of the Colorado River.',
        keyStats: [
            {
                value: '86%',
                label: "of Colorado's water goes to agriculture, supporting $5 billion in annual revenue",
                source: {
                    name: 'Colorado State University',
                    url: 'https://waterknowledge.colostate.edu/water-management-administration/water-uses/',
                },
            },
            {
                value: '560K',
                unit: 'acre-feet',
                label: 'projected annual water supply gap by 2050',
                source: {
                    name: 'CWCB Technical Update',
                    url: 'https://cwcb.colorado.gov/colorado-water-plan/technical-update-to-the-plan',
                },
            },
            {
                value: '40M+',
                label: 'people across 7 states depend on the Colorado River',
                source: {
                    name: 'Bureau of Reclamation',
                    url: 'https://www.usbr.gov/ColoradoRiverBasin/post2026/index.html',
                },
            },
            {
                value: '80/90',
                label: '80% of water on Western Slope, 90% of population on Front Range',
                source: {
                    name: 'CWCB Water Supply Planning',
                    url: 'https://cwcb.colorado.gov/focus-areas/supply/water-supply-planning',
                },
            },
        ],
        narrative: [
            {
                heading: 'A Headwaters State Under Pressure',
                text:
                    "Colorado's mountains generate an average of 16 million acre-feet of water annually, " +
                    'but roughly two-thirds must flow downstream to honor interstate compact obligations. ' +
                    "The state's population is projected to grow from 5.8 million to as many as 10 million " +
                    'by 2050, dramatically increasing demand for a resource that is already fully allocated.',
                sources: [
                    {
                        name: 'CWCB Water Supply Planning',
                        url: 'https://cwcb.colorado.gov/focus-areas/supply/water-supply-planning',
                    },
                    {
                        name: 'CWCB Technical Update',
                        url: 'https://cwcb.colorado.gov/colorado-water-plan/technical-update-to-the-plan',
                    },
                ],
            },
            {
                heading: 'Climate Change and the Colorado River',
                text:
                    'Since 2000, Colorado River flows have declined 16% below the 20th-century average, ' +
                    'and scientists increasingly describe this shift as \u201Caridification\u201D: a potentially ' +
                    'permanent change rather than a temporary drought. Snowpack, which provides the majority ' +
                    "of Colorado's water supply, is projected to decline 5\u201330% by 2050. Spring runoff " +
                    'is already arriving 1\u20134 weeks earlier, disrupting reservoir operations and agricultural planning.',
                sources: [
                    {
                        name: 'Colorado State University',
                        url: 'https://source.colostate.edu/western-river-compacts/',
                    },
                    {
                        name: 'CWCB Climate Focus Area',
                        url: 'https://cwcb.colorado.gov/focus-areas/hazards/climate',
                    },
                ],
            },
            {
                heading: 'Conservation and the Path Forward',
                text:
                    "Colorado's 2023 Water Plan lays out a comprehensive framework built on four pillars: " +
                    'Vibrant Communities, Robust Agriculture, Thriving Watersheds, and Resilient Planning. ' +
                    'The state has already reduced per-capita water use by 5%, and in 2025 the legislature ' +
                    'allocated $67 million for water projects statewide. But closing the projected supply gap ' +
                    'will require sustained collaboration between communities, agriculture, and policymakers.',
                sources: [
                    {
                        name: 'Colorado Water Plan',
                        url: 'https://cwcb.colorado.gov/colorado-water-plan',
                    },
                    {
                        name: '2025 Water Legislation',
                        url: 'https://cwcb.colorado.gov/news-articles/governor-polis-signs-bills-advancing-colorados-water-future',
                    },
                ],
            },
        ],
        learnMore: [
            {
                name: 'Colorado Water Plan',
                url: 'https://cwcb.colorado.gov/colorado-water-plan',
                description:
                    "The state's comprehensive framework for Colorado's water future",
            },
            {
                name: 'Colorado River Post-2026 Operations',
                url: 'https://www.usbr.gov/ColoradoRiverBasin/post2026/index.html',
                description:
                    'Federal planning for the next era of Colorado River management',
            },
            {
                name: 'Colorado Climate Center',
                url: 'https://climate.colostate.edu/',
                description:
                    'Climate research and drought monitoring from Colorado State University',
            },
            {
                name: 'Denver Water Climate Adaptation',
                url: 'https://www.denverwater.org/your-water/water-supply-and-planning/climate-change',
                description:
                    'How Denver is preparing for a changing water future',
            },
            {
                name: 'CWCB Interactive Water Supply Tool',
                url: 'https://cwcb.colorado.gov/news-articles/interactive-colorado-water-supply-tool-illuminates-statewide-water-needs-through-2050',
                description:
                    'Explore projected water supply needs across Colorado through 2050',
            },
        ],
    },

    dataSources: [
        {
            id: 1,
            title: 'Data Source Title',
            description: 'Short data source description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 2,
            title: 'Colorado Water Sustainability in Conservation',
            description: 'Short data product description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 3,
            title: 'Data Source Title',
            description: 'Short data source description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 4,
            title: 'Data Source Title',
            description: 'Short data source description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
        {
            id: 5,
            title: 'Data Source Title',
            description: 'Short data source description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            ctaText: 'Ask Bili about this dataset!',
            ctaLink: '#bili-chat',
        },
    ],

    dataProducts: [
        {
            id: 1,
            title: 'Data Product Title',
            description: 'Short data product description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Product',
                link: '#product-1',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 2,
            title: 'Water is Sustainability? StoryMap',
            description:
                'Balancing Today for a Better Tomorrow: Understanding the Three Pillars of Sustainability',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            featured: true,
            image: require('../../../assets/img/spotlights/storymap-preview.jpg'),
            primaryCTA: {
                text: 'View StoryMap',
                link: '#storymap',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 3,
            title: 'Data Product Title',
            description: 'Short data product description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Product',
                link: '#product-1',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 4,
            title: 'Data Product Title',
            description: 'Short data product description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Product',
                link: '#product-1',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
        {
            id: 5,
            title: 'Data Product Title',
            description: 'Short data product description (1–2 lines)',
            metadata: 'Metadata (Updated: Monthly, Records: 530K)',
            featured: false,
            image: null,
            primaryCTA: {
                text: 'View Product',
                link: '#product-1',
            },
            secondaryCTA: {
                text: 'Chat with Bili',
                link: '#bili-chat',
            },
        },
    ],

    starterPrompts: [
        'What are the main causes of water scarcity in Colorado?',
        'What policies could improve fair access to water in Colorado?',
        'How has climate change affected Colorado’s water supply?',
        'What are effective water conservation strategies for Colorado residents?',
        'What can I do to help conserve water in my community?',
        'How is climate change affecting water allocation?',
        'What are the biggest water challenges facing Colorado?',
    ],
};

export default waterSpotlightData;
